import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, keys, useMe } from '../../api.ts';
import { formatDate } from '../../../shared/format.ts';
import type { ReportResponse, TableReport } from '../../../shared/types.ts';
import { BandModeView, ReportTable } from '../../components/ReportTable.tsx';
import { ConfirmButton, ErrorBox, Loading, PageHeader, useDocumentTitle, useToast } from '../../components/ui.tsx';

const KIND_INFO: Record<string, string> = {
  divisions: 'DXCC-nykymaat. Kiinteät, käyttäjän itse ilmoittamat arvot ohittavat logista lasketut. 50 asemaa per divisioona.',
  cw: 'DXCC-nykymaat CW:llä. Kiinteät arvot ohittavat logista lasketut.',
  marathon: 'Nykymaat bandeittain 6–160 m (pl. 60 m). Suurempi arvo ilmoitetusta ja logista lasketusta.',
  band_modes: 'Nykymaat bandeittain CW ja PHONE 10–160 m sekä DIGI kaikilla bandeilla.',
};

export function ReportPage() {
  const id = Number(useParams().id);
  const { data: me } = useMe();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const q = useQuery({ queryKey: keys.report(id), queryFn: () => api.get<ReportResponse>(`/reports/${id}`) });
  useDocumentTitle(q.data?.name);

  const remove = useMutation({
    mutationFn: () => api.del(`/reports/${id}`),
    onSuccess: () => {
      toast.ok('Taulukko poistettu');
      void qc.invalidateQueries({ queryKey: keys.reports });
      navigate('/reports/search');
    },
    onError: toast.error,
  });

  const filtered = useMemo<TableReport | null>(() => {
    const d = q.data?.data;
    if (!d || d.type !== 'table') return null;
    const s = search.trim().toUpperCase();
    return s ? { ...d, rows: d.rows.filter((r) => String(r.userId).toUpperCase().includes(s)) } : d;
  }, [q.data, search]);

  if (q.isLoading) return <Loading text="Lasketaan taulukkoa…" />;
  if (q.error || !q.data) return <ErrorBox error={q.error} />;
  const r = q.data;

  return (
    <div className="page">
      <PageHeader
        title={r.name}
        sub={r.heading || KIND_INFO[r.kind]}
        actions={
          <>
            <a className="btn" href={`/api/reports/${id}/csv`} download>
              Lataa CSV
            </a>
            {me?.isAdmin && r.kind === 'custom' && (
              <Link className="btn" to={`/reports/search?from=${id}`}>
                Muokkaa hakuehtoja
              </Link>
            )}
            {me?.isAdmin && r.allowDelete && (
              <ConfirmButton title="Poista taulukko" message={`Haluatko varmasti poistaa taulukon "${r.name}"?`} confirmText="Poista" onConfirm={() => remove.mutateAsync()}>
                Poista
              </ConfirmButton>
            )}
          </>
        }
      />
      {r.compareDate && r.staleUsers.length > 0 && (
        <p className="muted small">
          * Käyttäjä ei ole päivittänyt kiinteitä OH-CW- ja Divisions-arvojaan {formatDate(r.compareDate)} jälkeen.
        </p>
      )}
      {r.data.type === 'band_modes' ? (
        <BandModeView data={r.data} linkCells={!!me} meId={me?.id} />
      ) : (
        <>
          <div className="row" style={{ marginBottom: 12 }}>
            <input className="input" style={{ maxWidth: 260 }} placeholder="Etsi kutsua…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <span className="muted small">{r.data.rows.length} asemaa</span>
          </div>
          <ReportTable
            data={filtered!}
            filters={r.filters}
            linkCells={!!me && r.kind !== 'divisions' && r.kind !== 'cw'}
            meId={me?.id}
            stale={r.staleUsers}
            divisions={r.kind === 'divisions' || r.kind === 'cw'}
          />
        </>
      )}
    </div>
  );
}
